import { useQuery } from '@tanstack/react-query';
import Input from '../../components/common/Input';
import ResponsiveSelect from '../../components/common/ResponsiveSelect';
import WarehouseUtilizationBar from '../../components/common/WarehouseUtilizationBar';
import { ClientOnboardingFormData } from '../../types';
import { clientService } from '../../services/clientService';
import { INPUT_SANITIZERS } from '../../utils/validationHelpers';
import { INDIAN_STATES } from '../../data/locations';

interface CompanyDetailsTabProps {
  formData: ClientOnboardingFormData;
  errors: Record<string, string>;
  onInputChange: (field: keyof ClientOnboardingFormData, value: any) => void;
}

export default function CompanyDetailsTab({ formData, errors, onInputChange }: CompanyDetailsTabProps) {
  // Fetch warehouse utilization to show available space
  const { data: warehouseUtilization, isLoading: isLoadingUtilization } = useQuery({
    queryKey: ['warehouse-utilization'],
    queryFn: () => clientService.getWarehouseUtilization(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Format handlers for specific fields with real-time validation
  const handleGSTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.gstFormat(e.target.value);
    if (sanitized.length <= 15) {
      onInputChange('gstNumber', sanitized);
    }
  };

  const handlePANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.panFormat(e.target.value);
    if (sanitized.length <= 10) {
      onInputChange('panNumber', sanitized);
    }
  };

  const handlePinCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.digitsOnly(e.target.value);
    if (sanitized.length <= 6) {
      onInputChange('pinCode', sanitized);
    }
  };

  const handleMobileChange = (field: keyof ClientOnboardingFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.digitsOnly(e.target.value);
    if (sanitized.length <= 10) {
      onInputChange(field, sanitized);
    }
  };

  const handleContactPersonNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.contactPersonNameFormat(e.target.value);
    onInputChange('contactPersonName', sanitized);
  };

  const handleCityNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.cityNameFormat(e.target.value);
    onInputChange('city', sanitized);
  };

  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.companyNameFormat(e.target.value);
    onInputChange('companyName', sanitized);
  };

  const handleDesignationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.designationFormat(e.target.value);
    onInputChange('designation', sanitized);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = INPUT_SANITIZERS.emailFormat(e.target.value);
    onInputChange('email', sanitized);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h2>
      
      {/* Warehouse Utilization Bar - Show available space */}
      <div className="mb-6">
        <WarehouseUtilizationBar
          utilization={warehouseUtilization ?? null}
          isLoading={isLoadingUtilization}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Company Name"
          value={formData.companyName}
          onChange={handleCompanyNameChange}
          required
          error={errors.companyName}
          placeholder="e.g., Acme Logistics"
          maxLength={100}
        />
        
        <Input
          label="Registration Number"
          value={formData.registrationNumber}
          onChange={(e) => {
            const sanitized = INPUT_SANITIZERS.registrationNumberFormat(e.target.value);
            onInputChange('registrationNumber', sanitized);
          }}
          error={errors.registrationNumber}
          placeholder="e.g., REG2024001 or CIN123456"
          maxLength={15}
        />
        
        <div>
          <Input
            label="GST Number"
            value={formData.gstNumber}
            onChange={handleGSTChange}
            required
            error={errors.gstNumber}
            placeholder="27AAAAA0000A1Z5"
            maxLength={15}
          />
        </div>
        
        <div>
          <Input
            label="PAN Number"
            value={formData.panNumber}
            onChange={handlePANChange}
            required
            error={errors.panNumber}
            placeholder="ABCDE1234F"
            maxLength={10}
          />
        </div>
      </div>

      <Input
        label="Address"
        value={formData.address}
        onChange={(e) => {
          const sanitized = INPUT_SANITIZERS.addressFormat(e.target.value);
          onInputChange('address', sanitized);
        }}
        required
        error={errors.address}
        placeholder="e.g., Unit 21, Industrial Area, Phase 2, Sector 15"
        maxLength={200}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Input
          label="City"
          value={formData.city}
          onChange={handleCityNameChange}
          required
          error={errors.city}
          placeholder="Enter city"
          maxLength={100}
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <ResponsiveSelect
            options={INDIAN_STATES.map((state) => ({ value: state.value, label: state.label }))}
            value={formData.state}
            onChange={(val) => onInputChange('state', val)}
            placeholder="Select state"
            className={`
              w-full px-3 py-2 border rounded-lg
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent
              transition-colors duration-200
              ${errors.state ? 'border-red-500' : 'border-gray-300'}
            `}
          />
          {errors.state && (
            <p className="mt-1 text-sm text-red-600">{errors.state}</p>
          )}
        </div>
        
        <Input
          label="PIN Code"
          value={formData.pinCode}
          onChange={handlePinCodeChange}
          required
          error={errors.pinCode}
          placeholder="e.g., 400001"
          maxLength={6}
        />
        
        <Input
          label="Country"
          value="India"
          onChange={() => {}} // No-op since it's read-only
          required
          disabled
          error={errors.country}
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Contact Person Name"
            value={formData.contactPersonName}
            onChange={handleContactPersonNameChange}
            required
            error={errors.contactPersonName}
            placeholder="e.g., Rahul Mehta"
            maxLength={100}
          />
          
          <Input
            label="Designation"
            value={formData.designation}
            onChange={handleDesignationChange}
            error={errors.designation}
            placeholder="e.g., Operations Manager"
            maxLength={30}
          />
          
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleEmailChange}
            required
            error={errors.email}
            placeholder="e.g., contact@company.com"
          />
          
          <Input
            label="Mobile Number"
            type="tel"
            value={formData.phone}
            onChange={handleMobileChange('phone')}
            required
            error={errors.phone}
            placeholder="e.g., 9999999999"
            maxLength={10}
          />
          
          <Input
            label="Alternate Mobile Number"
            type="tel"
            value={formData.alternatePhone}
            onChange={handleMobileChange('alternatePhone')}
            error={errors.alternatePhone}
            placeholder="e.g., 8888888888"
            maxLength={10}
          />
        </div>
      </div>
    </div>
  );
}